import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from './api';
import { InviteResponseError, LoginResponse } from './types';
import { parseUrlParams } from './utils/urlParams';
import background from './assets/homepage-background.webp';
import logo from './assets/youcould_logo.png';
import plane from './assets/plane.svg';
import './LoginPage.css';

function App() {
  const navigate = useNavigate();
  const [isHandlingLogin, setIsHandlingLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const canSubmit = Boolean(username.trim() && password.trim()) && !isHandlingLogin;

  useEffect(() => {
    const loadUrlParams = async () => {
      const urlParams = await parseUrlParams();

      if (urlParams.access_token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, urlParams.access_token);
        if (urlParams.width) localStorage.setItem('overlay_width', urlParams.width);
        if (urlParams.height) localStorage.setItem('overlay_height', urlParams.height);
        if (urlParams.refresh_frequency) {
          localStorage.setItem('overlay_refresh_frequency', urlParams.refresh_frequency);
        }
        if (urlParams.show_count) localStorage.setItem('overlay_show_count', urlParams.show_count);
        if (urlParams.show_count_extended) {
          localStorage.setItem('overlay_show_count_extended', urlParams.show_count_extended);
        }
        if (urlParams.theme) localStorage.setItem('overlay_theme', urlParams.theme);
        if (urlParams.opacity) localStorage.setItem('overlay_opacity', urlParams.opacity);

        navigate('/opps');
      }
    };

    void loadUrlParams();
  }, [navigate]);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password.');
      return;
    }

    setIsHandlingLogin(true);
    setError('');

    try {
      const response: LoginResponse = await login(username, password);
      if (response?.access_token) {
        localStorage.setItem(ACCESS_TOKEN_KEY, response.access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
        navigate('/settings');
      } else {
        const errorResponse = response as unknown as InviteResponseError;
        setError(errorResponse?.detail?.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsHandlingLogin(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleLogin();
  };

  return (
    <div className="login-page">
      <div
        aria-hidden
        className="login-page__background"
        style={{ backgroundImage: `url(${background})` }}
      />
      <div aria-hidden className="login-page__overlay" />

      <div className="login-page__content">
        <header className="login-header">
          <div className="login-header__inner">
            <img src={logo} alt="YouCould" className="login-header__logo" />
            <span className="login-header__tagline">Content defined by you</span>
          </div>
        </header>

        <main className="login-main">
          <div className="login-main__inner">
            <h1 className="login-title">
              <span className="login-title__gradient">Twitch Overlay</span>
            </h1>

            <div className="login-panel-wrap">
              <div className="login-panel" id="login">
                <div className="login-panel__brand">
                  <img src={logo} alt="YouCould" className="login-panel__brand-logo" />
                  <p className="login-panel__brand-tagline">Content defined by you</p>
                </div>

                <h2 className="login-panel__heading">Welcome back</h2>
                <p className="login-panel__subtitle">Sign in to your account</p>

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="login-field">
                    <input
                      id="login-username"
                      type="text"
                      autoComplete="username"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder="Username"
                    />
                  </div>

                  <div className="login-field">
                    <input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Password"
                    />
                  </div>

                  {error ? <p className="login-error">{error}</p> : null}

                  <button type="submit" className="login-submit" disabled={!canSubmit}>
                    {isHandlingLogin ? (
                      <span className="login-spinner" aria-label="Signing in" />
                    ) : (
                      <>
                        Sign in
                        <img src={plane} alt="" className="login-submit__icon" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
